// PlantArt.jsx
// Hand-rolled SVG art for Bloom & Blight: one cute potted plant per species,
// plus the 4-petal victory flower shown in each gardener's HUD.

import React from "react";

const POT = (
  <g>
    <path d="M30 82 L70 82 L64 104 L36 104 Z" fill="#d97e5a" />
    <rect x="27" y="78" width="46" height="7" rx="3.5" fill="#c96b48" />
  </g>
);

const Bloomed = () => (
  <g>
    <g transform="translate(50 22)">
      {[0, 72, 144, 216, 288].map((r) => (
        <ellipse
          key={r}
          rx="6"
          ry="10"
          fill="#ec6d97"
          transform={`rotate(${r}) translate(0 -9)`}
        />
      ))}
      <circle r="5" fill="#ffd166" />
    </g>
    <circle cx="22" cy="34" r="2" fill="#ffd166" />
    <circle cx="79" cy="28" r="2.4" fill="#ec6d97" />
    <circle cx="72" cy="48" r="1.7" fill="#ffd166" />
  </g>
);

const ART = {
  calathea: (bloom) => (
    <g>
      {[
        [-38, 26], [-19, 16], [0, 12], [19, 16], [38, 26],
      ].map(([rot, ry], i) => (
        <g key={i} transform={`translate(50 78) rotate(${rot})`}>
          <ellipse cx="0" cy={-24 - ry / 2} rx="10" ry={14 + ry / 2} fill="#609a48" />
          <ellipse cx="0" cy={-24 - ry / 2} rx="6" ry={9 + ry / 2} fill="#3b6224" opacity="0.5" />
          <line x1="0" y1="-12" x2="0" y2={-34 - ry} stroke="#eaf4e2" strokeWidth="1.6" />
        </g>
      ))}
      {POT}
      {bloom && <Bloomed />}
    </g>
  ),
  pilea: (bloom) => (
    <g>
      {[
        [-24, 34, 9], [-13, 22, 11], [0, 16, 12], [13, 24, 10], [24, 38, 8],
      ].map(([dx, dy, r], i) => (
        <g key={i}>
          <path
            d={`M50 80 Q ${50 + dx / 2} ${70 - dy / 2} ${50 + dx} ${44 + dy / 2}`}
            stroke="#4c7c34"
            strokeWidth="2.5"
            fill="none"
          />
          <circle cx={50 + dx} cy={40 + dy / 2} r={r} fill="#609a48" />
          <circle cx={50 + dx - r / 3} cy={38 + dy / 2} r={r / 3.2} fill="#8fbf78" />
        </g>
      ))}
      {POT}
      {bloom && <Bloomed />}
    </g>
  ),
  snake: (bloom) => (
    <g>
      {[
        [-14, 46, -6], [-5, 60, -2], [4, 54, 3], [13, 42, 7],
      ].map(([dx, h, lean], i) => (
        <g key={i}>
          <path
            d={`M${50 + dx - 5} 82 Q ${50 + dx + lean} ${82 - h * 0.6} ${50 + dx + lean} ${82 - h} Q ${50 + dx + lean + 2} ${82 - h * 0.55} ${50 + dx + 5} 82 Z`}
            fill="#4c7c34"
          />
          <path
            d={`M${50 + dx - 2} 82 Q ${50 + dx + lean} ${82 - h * 0.6} ${50 + dx + lean} ${82 - h + 4}`}
            stroke="#c9d96b"
            strokeWidth="1.8"
            fill="none"
          />
        </g>
      ))}
      {POT}
      {bloom && <Bloomed />}
    </g>
  ),
  cactus: (bloom) => (
    <g>
      <rect x="41" y="34" width="18" height="50" rx="9" fill="#7cb86a" />
      <path d="M41 58 Q28 58 28 46 L28 40 Q28 36 32 36 Q36 36 36 40 L36 46 Q36 50 42 50" fill="#7cb86a" />
      <path d="M59 66 Q72 66 72 54 L72 50 Q72 46 68 46 Q64 46 64 50 L64 54 Q64 58 58 58" fill="#7cb86a" />
      {[40, 50, 60, 70].map((y) => (
        <g key={y} stroke="#eaf4e2" strokeWidth="1.4" strokeLinecap="round">
          <line x1="45" y1={y} x2="45" y2={y + 2.6} />
          <line x1="55" y1={y + 4} x2="55" y2={y + 6.6} />
        </g>
      ))}
      {!bloom && <circle cx="50" cy="32" r="4" fill="#ec6d97" opacity="0.85" />}
      {POT}
      {bloom && <Bloomed />}
    </g>
  ),
  zz: (bloom) => (
    <g>
      {[
        [-1, -26, 52], [1, 4, 46], [-1, 26, 40],
      ].map(([dir, spread, h], i) => (
        <g key={i}>
          <path
            d={`M50 82 Q ${50 + spread * 0.6} ${78 - h * 0.5} ${50 + spread} ${82 - h}`}
            stroke="#4c7c34"
            strokeWidth="2.6"
            fill="none"
          />
          {[0.35, 0.55, 0.75, 0.95].map((t, j) => {
            const x = 50 + spread * 0.6 * t + spread * 0.4 * t * t;
            const y = 82 - (h * 0.5 * t + h * 0.5 * t * t) * 0.95;
            return (
              <g key={j}>
                <ellipse cx={x - 5} cy={y} rx="5.5" ry="3.6" fill="#609a48"
                  transform={`rotate(${-28 * dir} ${x - 5} ${y})`} />
                <ellipse cx={x + 5} cy={y - 2} rx="5.5" ry="3.6" fill="#3b6224"
                  transform={`rotate(${30 * dir} ${x + 5} ${y - 2})`} />
              </g>
            );
          })}
        </g>
      ))}
      {POT}
      {bloom && <Bloomed />}
    </g>
  ),
};

export const PlantArt = ({ speciesId, bloom = false, className = "" }) => (
  <svg viewBox="0 0 100 110" className={className} aria-hidden="true">
    {(ART[speciesId] || ART.zz)(bloom)}
  </svg>
);

/*******************************************************************************
 * PetalFlower — the win-condition HUD. `total` petals around a golden center
 * (evenly spaced); each grown plant fills one in pink.
 ******************************************************************************/
export const PetalFlower = ({ petals = 0, total = 5, size = 54 }) => (
  <svg viewBox="0 0 60 60" width={size} height={size} aria-label={`${petals} of ${total} petals`}>
    {Array.from({ length: total }, (_, i) => {
      const rot = (i * 360) / total;
      return (
        <ellipse
          key={i}
          cx="30"
          cy="30"
          rx="8"
          ry="13.5"
          transform={`rotate(${rot} 30 30) translate(0 -13)`}
          fill={i < petals ? "#ec6d97" : "#fff7e8"}
          stroke={i < petals ? "#d4547e" : "#c9b18f"}
          strokeWidth="1.6"
          strokeDasharray={i < petals ? "none" : "3 2.4"}
          className={i === petals - 1 ? "bnb-petal-pop" : ""}
        />
      );
    })}
    <circle cx="30" cy="30" r="7.5" fill="#ffd166" stroke="#e0a93e" strokeWidth="1.6" />
  </svg>
);
